import { type FC, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Sheet — a Radix Dialog rendered as a side panel with glass skin.
 *
 * Handles focus trap, Esc, scroll lock, and backdrop click correctly via
 * Radix. Do NOT hand-roll a sheet; Radix's a11y behaviour is nontrivial
 * and broken hand-rolls cause real usability issues, especially on iOS.
 */

export interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  side?: 'left' | 'right';
  title: string;
  widthPx?: number;
  children: ReactNode;
}

export const Sheet: FC<SheetProps> = ({
  open,
  onOpenChange,
  side = 'right',
  title,
  widthPx = 340,
  children,
}) => {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-slate-900/25 backdrop-blur-sm animate-fade-in" />
        <Dialog.Content
          className={cn(
            'fixed z-50 top-3 bottom-3 liquid-glass-strong rounded-[22px] p-5 overflow-y-auto scrollbar-subtle',
            side === 'right'
              ? 'right-3 animate-slide-in-right'
              : 'left-3 animate-slide-in-right',
          )}
          style={{
            width: widthPx,
            maxWidth: 'calc(100vw - 1.5rem)',
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <Dialog.Title className="text-heading font-semibold text-text-primary tracking-tight">
              {title}
            </Dialog.Title>
            <Dialog.Close
              aria-label="Close"
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-900/5 hover:bg-slate-900/10 transition-colors"
            >
              <X className="w-4 h-4 text-text-secondary" />
            </Dialog.Close>
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default Sheet;
